/**
 * Voies et motifs d'un arrêté — M14 + M15 (retour Manon, mi-parcours).
 *
 * « Sur un même arrêté, plusieurs demandes » : plusieurs voies (une
 * manifestation qui ferme trois rues) et plusieurs motifs (travaux +
 * stationnement + circulation) dans UN arrêté. Deux listes éditables,
 * ligne à ligne ; elles s'impriment sous l'objet du PDF.
 */
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui';

export interface Voie { voie: string; precision?: string | null }

interface Props {
  localisations: Voie[];
  motifs: string[];
  onLocalisations: (v: Voie[]) => void;
  onMotifs: (m: string[]) => void;
}

const MOTIFS_COURANTS = [
  'Travaux de voirie', 'Interdiction de stationner', 'Interdiction de circuler',
  'Manifestation', 'Déménagement', 'Occupation du domaine public',
];

export function VoiesMotifsEditor({ localisations, motifs, onLocalisations, onMotifs }: Props) {
  const majVoie = (i: number, champ: keyof Voie, val: string) =>
    onLocalisations(localisations.map((l, k) => (k === i ? { ...l, [champ]: val } : l)));

  return (
    <div className="space-y-4 rounded-md border border-border/60 p-3">
      <div>
        <p className="text-sm font-medium text-ink">Voies concernées</p>
        <p className="mb-2 text-xs text-ink-muted">
          Une ligne par rue ou tronçon — un seul arrêté peut en couvrir plusieurs.
        </p>
        <div className="space-y-2">
          {localisations.map((l, i) => (
            <div key={i} className="flex gap-2">
              <Input aria-label={`Voie ${i + 1}`} value={l.voie} maxLength={200}
                placeholder="Ex : rue du Port" onChange={(e) => majVoie(i, 'voie', e.target.value)} />
              <Input aria-label={`Précision voie ${i + 1}`} value={l.precision ?? ''} maxLength={200}
                placeholder="Tronçon (facultatif) : du n°2 au n°18"
                onChange={(e) => majVoie(i, 'precision', e.target.value)} />
              <button type="button" aria-label={`Retirer la voie ${i + 1}`}
                className="shrink-0 rounded-md px-2 text-ink-muted hover:text-urgent"
                onClick={() => onLocalisations(localisations.filter((_, k) => k !== i))}>
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
        {localisations.length < 30 && (
          <button type="button" className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline"
            onClick={() => onLocalisations([...localisations, { voie: '', precision: '' }])}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Ajouter une voie
          </button>
        )}
      </div>

      <div>
        <p className="text-sm font-medium text-ink">Motifs</p>
        <p className="mb-2 text-xs text-ink-muted">
          Plusieurs motifs peuvent être regroupés sur le même arrêté.
        </p>
        <div className="space-y-2">
          {motifs.map((m, i) => (
            <div key={i} className="flex gap-2">
              <Input aria-label={`Motif ${i + 1}`} value={m} maxLength={200}
                list="motifs-courants"
                onChange={(e) => onMotifs(motifs.map((x, k) => (k === i ? e.target.value : x)))} />
              <button type="button" aria-label={`Retirer le motif ${i + 1}`}
                className="shrink-0 rounded-md px-2 text-ink-muted hover:text-urgent"
                onClick={() => onMotifs(motifs.filter((_, k) => k !== i))}>
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
        <datalist id="motifs-courants">
          {MOTIFS_COURANTS.map((m) => <option key={m} value={m} />)}
        </datalist>
        {motifs.length < 10 && (
          <button type="button" className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline"
            onClick={() => onMotifs([...motifs, ''])}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Ajouter un motif
          </button>
        )}
      </div>
    </div>
  );
}

/** Nettoie les listes avant envoi : lignes vides retirées, espaces rognés. */
export function nettoyerVoiesMotifs(localisations: Voie[], motifs: string[]) {
  return {
    localisations: localisations
      .map((l) => ({ voie: l.voie.trim(), precision: (l.precision ?? '').trim() || null }))
      .filter((l) => l.voie.length >= 2),
    motifs: motifs.map((m) => m.trim()).filter((m) => m.length >= 2),
  };
}
